import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getErrorMessage } from '../utils/api';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const [autoLoggedOut] = useState(() => {
    const r = sessionStorage.getItem('logout_reason') === 'auto';
    sessionStorage.removeItem('logout_reason');
    return r;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password });
      
      // Store token in localStorage for API authentication
      // Backend returns 'session_token', not 'token'
      const token = response.data.session_token || response.data.token;
      if (token) {
        localStorage.setItem('token', token);
      }
      
      login(response.data.user);
      toast.success('Logged in successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8] p-4">
      <Card className="w-full max-w-md p-8 bg-white border border-[#E5E5DF] rounded-lg shadow-sm" data-testid="login-card">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_trial-batch-hub/artifacts/q9rnwiie_logo%20c.jpg" 
              alt="CERACARE Logo" 
              className="h-24 w-auto object-contain"
            />
          </div>
          <h1 className="text-4xl font-light tracking-tight text-[#2C4C3B] mb-2">CERACARE</h1>
          <p className="text-sm text-[#1C1C1A]/60">Batch Management System</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {autoLoggedOut && (
            <div data-testid="auto-logout-notice" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              You were logged out for security because the app was left or minimised. Please sign in again.
            </div>
          )}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A] mb-2">
              Email
            </label>
            <Input
              data-testid="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="staff@example.com"
              required
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A] mb-2">
              Password
            </label>
            <Input
              data-testid="password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="w-full"
            />
          </div>

          <Button
            data-testid="login-button"
            type="submit"
            disabled={loading}
            className="w-full bg-[#2C4C3B] hover:bg-[#2C4C3B]/90 text-white rounded-md py-2 transition-colors"
          >
            {loading ? 'Processing...' : 'Login'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default Login;